import { borderRadius, Button, color, cssBase, cx, IconAdd, IconClose, spacing, styled } from '@lessonup/ui-components';
import { generateHTML, JSONContent } from '@tiptap/core';
import React, { useRef } from 'react';
import { fillInTheBlanksViewerExtensions } from './FillInTheBlanksTiptapViewer';

export interface FillInTheBlanksWordBankBlank {
  type: 'blank';
  id?: string; // only used in editor
  word: JSONContent;
}

export interface FillInTheBlanksWordBankDistractor {
  type: 'distractor';
  word: string;
}

export type FillInTheBlanksWordBankItem = FillInTheBlanksWordBankBlank | FillInTheBlanksWordBankDistractor;

export interface FillInTheBlanksWordBankViewerProps {
  items: FillInTheBlanksWordBankItem[];
  handleWordbankAdd?: (ref: React.RefObject<HTMLButtonElement | null>) => void;
  hideIndices?: number[];
  sellectedId?: string;
  onWordClick?: (word: FillInTheBlanksWordBankItem) => void;
  onMouseEnter?: (word: FillInTheBlanksWordBankItem) => void;
  onMouseLeave?: (word: FillInTheBlanksWordBankItem) => void;
  onRemoveWord?: (word: FillInTheBlanksWordBankItem, index: number) => void;
}

export const FillInTheBlanksWordBank: React.FC<FillInTheBlanksWordBankViewerProps> = ({
  items: words,
  onWordClick,
  onMouseEnter,
  onMouseLeave,
  onRemoveWord,
  handleWordbankAdd,
  hideIndices,
  sellectedId,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const htmlWords = words.map((word, index) => {
    const html =
      typeof word.word === 'string'
        ? `<div>${word.word}</div>`
        : generateHTML(word.word, fillInTheBlanksViewerExtensions);

    return {
      word,
      html,
      index,
    };
  });

  const wordsToRender = htmlWords.filter((word) => !hideIndices?.includes(word.index));

  const handleAddDistractor = () => {
    handleWordbankAdd?.(buttonRef);
  };

  return (
    <WordBankWrapper>
      {handleWordbankAdd && (
        <StyledButton
          ref={buttonRef}
          size="small"
          buttonType="secondary"
          showStroke
          buttonShape="rect"
          onClick={handleAddDistractor}
          iconStart={<IconAdd />}
          disabled={words.length >= 100}
        />
      )}
      {handleWordbankAdd && <ButtonSpacer />}
      {wordsToRender.map((word) => (
        <WordBankItem
          key={`${word}-${word.index}`}
          onClick={() => onWordClick?.(word.word)}
          distractor={word.word.type === 'distractor'}
          onMouseEnter={() => onMouseEnter?.(word.word)}
          onMouseLeave={() => onMouseLeave?.(word.word)}
          className={cx(word.word.type === 'blank' && !!sellectedId && sellectedId === word.word.id && activeCss)}
        >
          <span dangerouslySetInnerHTML={{ __html: word.html }} />
          {onRemoveWord && (
            <Button
              size="small"
              buttonType="neutral"
              buttonShape="rect"
              iconStart={<IconClose />}
              onClick={() => onRemoveWord(word.word, word.index)}
            />
          )}
        </WordBankItem>
      ))}
    </WordBankWrapper>
  );
};

const WordBankWrapper = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.size8};
  padding: ${spacing.size6};
  background-color: ${color.neutralNew.surface.fillSubjacent};
  border-radius: ${borderRadius.rounded8};
  min-height: 30px;
  align-items: center;
  justify-content: center;
  padding-left: ${spacing.size24};
  padding-right: ${spacing.size24};
`;

const StyledButton = styled(Button)`
  position: absolute;
  top: 8px;
  left: 8px;
`;

const ButtonSpacer = styled.div`
  width: 40px;
  height: 32px;
  flex-shrink: 0;
`;

const WordBankItem = styled.div<{ distractor?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: ${color.neutral.surface.background};
  border: 2px ${({ distractor }) => (distractor ? 'dashed' : `solid`)} ${color.state.disabled.stroke};
  padding-left: 0.25rem;
  padding-right: 0.25rem;
  border-radius: 0.25rem;
  line-height: 1.4;
  text-align: center;
  color: ${color.neutral.surface.text};
  &:hover {
    background-color: ${color.neutral.surface.hover};
    button {
      background-color: ${color.neutral.surface.hover};
    }
  }
`;

const activeCss = cssBase`
  background-color: ${color.state.active.fill} !important;
  border-color: ${color.accent.secondary.background} !important;
  button {
    background-color: ${color.state.active.fill} !important;
  }
`;
